import {Ref} from 'react';
import {Box, BoxProps, CloseButton, IconButton, Input, InputGroup} from '@chakra-ui/react';
import {Search} from 'lucide-react';

interface SearchFieldProps extends Omit<BoxProps, 'onChange'> {
    value: string;
    onChange: (value: string) => void;
    /** Phones show the field only while open (opened by `SearchToggleButton`); larger screens always show it. */
    isOpen: boolean;
    /** A search is narrowing the listing: the field turns pale blue, as the filter button does. */
    isActive: boolean;
    onClose: () => void;
    inputRef?: Ref<HTMLInputElement>;
}

/** The folder view's search field (the matching itself is `searchByName`). */
export function SearchField({value, onChange, isOpen, isActive, onClose, inputRef, ...boxProps}: SearchFieldProps) {
    return <Box display={{base: isOpen ? 'block' : 'none', md: 'block'}} {...boxProps}>
        <InputGroup
            startElement={<Search size={16}/>}
            startElementProps={{color: isActive ? 'blue.600' : undefined}}
            endElement={value &&
                <CloseButton size="xs" variant="plain" aria-label="Keresés törlése" onClick={() => onChange('')}/>
            }
        >
            <Input
                ref={inputRef}
                size="sm"
                type="search"
                placeholder="Keresés a mappában"
                aria-label="Keresés a mappában"
                value={value}
                onChange={(e) => onChange(e.target.value)}
                bg={isActive ? 'blue.50' : undefined}
                borderColor={isActive ? 'blue.300' : undefined}
                onKeyDown={(e) => {
                    if (e.key !== 'Escape') return;
                    // The first Esc clears the search, the next leaves the field.
                    if (value) {
                        onChange('');
                    } else {
                        e.currentTarget.blur();
                        onClose();
                    }
                }}
                // The browser's own clear button would duplicate ours.
                css={{'&::-webkit-search-cancel-button': {display: 'none'}}}
            />
        </InputGroup>
    </Box>;
}

interface SearchToggleButtonProps {
    isOpen: boolean;
    /** A search is narrowing the listing. */
    isActive: boolean;
    onToggle: () => void;
}

/** Opens and closes the search field on phones, where it doesn't fit next to the other buttons. */
export function SearchToggleButton({isOpen, isActive, onToggle}: SearchToggleButtonProps) {
    return <IconButton
        size="sm"
        hideFrom="md"
        variant={isOpen || isActive ? 'subtle' : 'outline'}
        colorPalette={isActive ? 'blue' : 'gray'}
        aria-label="Keresés"
        aria-expanded={isOpen}
        title="Keresés (F)"
        onClick={onToggle}
    >
        <Search size={16}/>
    </IconButton>;
}
